import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Save, RotateCcw, ListChecks } from "lucide-react";
import api from "@/lib/api";
import { Switch } from "@/components/ui/switch";

const FIXED = (f) => f.type === "signature" || f.readonly;

// Admin/Manager: pro V-Check-Feld festlegen, ob Pflicht und ob „Entfällt“ erlaubt ist.
export default function AdminVCheckRules() {
  const navigate = useNavigate();
  const [defaults, setDefaults] = useState([]);
  const [rules, setRules] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    api.get("/admin/vcheck-wp/field-rules")
      .then((r) => { setDefaults(r.data.default_schema || []); setRules(r.data.rules || {}); })
      .catch((e) => toast.error(e.response?.data?.detail || "Regeln konnten nicht geladen werden"))
      .finally(() => setLoading(false));
  }, []);

  const eff = (f) => ({
    required: rules[f.key]?.required ?? !!f.required,
    na_allowed: rules[f.key]?.na_allowed ?? true,
  });
  const setRule = (f, patch) => setRules((prev) => ({ ...prev, [f.key]: { ...eff(f), ...patch } }));

  const stats = useMemo(() => {
    let req = 0; let total = 0;
    defaults.forEach((s) => s.fields.forEach((f) => { if (!FIXED(f)) { total += 1; if (eff(f).required) req += 1; } }));
    return { req, total };
  }, [defaults, rules]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/vcheck-wp/field-rules", { rules });
      toast.success("V-Check-Pflichtfelder gespeichert");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setSaving(false); }
  };

  if (loading) {
    return <div className="min-h-screen bg-[#09090B] flex items-center justify-center"><Loader2 className="animate-spin text-[#00E575]" size={28} /></div>;
  }

  const q = filter.trim().toLowerCase();
  return (
    <div className="min-h-screen bg-[#09090B] text-zinc-50" data-testid="admin-vcheck-rules">
      <div className="max-w-5xl mx-auto px-4 sm:px-8 py-6 pb-28">
        <button onClick={() => navigate(-1)} className="h-10 w-10 rounded-xl bg-zinc-800 flex items-center justify-center mb-4" data-testid="vcheck-rules-back">
          <ArrowLeft size={18} />
        </button>
        <h1 className="font-display text-2xl sm:text-3xl font-bold inline-flex items-center gap-2"><ListChecks className="text-[#00E575]" /> V-Check Pflichtfelder</h1>
        <p className="text-zinc-400 mt-1 text-sm">
          Legen Sie für Ihre Firma fest, welche Angaben im V-Check Wärmepumpe Pflicht sind (rot bis erledigt) und bei welchen
          der Button „Entfällt“ erlaubt ist. Gilt für alle Projekte des Mandanten.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <span className="text-sm text-zinc-300" data-testid="vcheck-rules-count"><b className="text-[#00E575]">{stats.req}</b> von {stats.total} Feldern sind Pflicht</span>
          <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Feld suchen …"
                 data-testid="vcheck-rules-search"
                 className="h-10 flex-1 min-w-[180px] px-3 rounded-lg bg-zinc-900 border border-zinc-700 text-base" />
          <button onClick={() => setRules({})} data-testid="vcheck-rules-reset"
                  className="h-10 px-3 rounded-lg bg-zinc-800 text-zinc-200 text-sm inline-flex items-center gap-1.5">
            <RotateCcw size={14} /> Standard
          </button>
        </div>

        <div className="mt-6 space-y-5">
          {defaults.map((sec) => {
            const fields = sec.fields.filter((f) => !FIXED(f) && (!q || f.label.toLowerCase().includes(q)));
            if (!fields.length) return null;
            return (
              <section key={sec.section} className="rounded-2xl border border-zinc-800 bg-[#101015] overflow-hidden">
                <div className="px-4 py-3 border-b border-zinc-800 font-semibold">
                  <span className="font-mono text-[#00E575] mr-2">{sec.section}</span>{sec.title}
                </div>
                <div className="divide-y divide-zinc-800">
                  {fields.map((f) => {
                    const e = eff(f);
                    return (
                      <div key={f.key} className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4" data-testid={`vcheck-rule-${f.key}`}>
                        <div className="flex-1 min-w-0">
                          <div className="text-[14px] text-zinc-100">{f.label}</div>
                          <div className="text-[11.5px] text-zinc-500">
                            {f.type === "photo_block" ? "Foto-Block" : "Feld"} · Standard: {f.required ? "Pflicht" : "optional"}
                            {f.show_if && " · nur bei Bedingung sichtbar"}
                          </div>
                        </div>
                        <label className="flex items-center gap-2 text-[13px] text-zinc-300">
                          <Switch checked={e.required} onCheckedChange={(v) => setRule(f, { required: v })} data-testid={`vcheck-rule-required-${f.key}`} />
                          Pflicht
                        </label>
                        <label className="flex items-center gap-2 text-[13px] text-zinc-300">
                          <Switch checked={e.na_allowed} onCheckedChange={(v) => setRule(f, { na_allowed: v })} data-testid={`vcheck-rule-na-${f.key}`} />
                          „Entfällt“ erlaubt
                        </label>
                      </div>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>
      </div>
      <div className="fixed bottom-0 left-0 right-0 bg-[#09090B]/95 backdrop-blur border-t border-zinc-800 p-4 z-30" data-bottom-bar>
        <div className="max-w-5xl mx-auto flex justify-end">
          <button onClick={save} disabled={saving} data-testid="vcheck-rules-save"
                  className="h-12 px-6 rounded-xl bg-[#00E575] text-zinc-950 font-bold inline-flex items-center gap-2 disabled:opacity-60">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Speichern
          </button>
        </div>
      </div>
    </div>
  );
}
